import {
  priorityLabels,
  statusLabels,
  categoryLabels,
  type SupportTicket,
} from '@/mocks/superAdminSupport';

interface SupportTableProps {
  tickets: SupportTicket[];
  onView: (ticket: SupportTicket) => void;
}

const priorityColorMap: Record<string, string> = {
  low: 'bg-secondary-100 text-secondary-700',
  normal: 'bg-sky-50 text-sky-600',
  high: 'bg-amber-50 text-amber-700',
  urgent: 'bg-red-50 text-red-500',
};

const statusColorMap: Record<string, string> = {
  open: 'bg-secondary-100 text-secondary-700',
  in_progress: 'bg-sky-50 text-sky-600',
  waiting_customer: 'bg-amber-50 text-amber-700',
  resolved: 'bg-emerald-50 text-emerald-600',
  closed: 'bg-background-200 text-foreground-500',
};

const statusIcons: Record<string, string> = {
  open: 'ri-checkbox-blank-circle-line',
  in_progress: 'ri-loader-4-line',
  waiting_customer: 'ri-user-voice-line',
  resolved: 'ri-check-double-line',
  closed: 'ri-close-circle-line',
};

const categoryAbbr: Record<string, string> = {
  technical: 'Técnico',
  billing: 'Facturación',
  account: 'Cuenta',
  content: 'Contenido',
  frequency: 'Frecuencia',
  moderation: 'Moderación',
  other: 'Otro',
};

function formatDate(d: string): string {
  const date = new Date(d);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const mins = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${mins}`;
}

function timeAgo(d: string): string {
  const now = new Date();
  const date = new Date(d);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `Hace ${diffMins} min`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `Hace ${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  return `Hace ${diffDays}d`;
}

export default function SupportTable({ tickets, onView }: SupportTableProps) {
  if (tickets.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
          <i className="ri-customer-service-2-line text-foreground-300 text-2xl"></i>
        </div>
        <p className="text-foreground-800 text-sm font-semibold mb-1">Sin tickets</p>
        <p className="text-foreground-400 text-xs">No hay tickets que coincidan con los filtros actuales.</p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop table */}
      <div className="hidden lg:block bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
        <table className="w-full">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/50">
                <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Ticket</th>
                <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Radio</th>
                <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Asunto</th>
                <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Categoría</th>
                <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Prioridad</th>
                <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Estado</th>
                <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Asignado</th>
                <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Creado</th>
                <th className="text-center px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider w-16">Ver</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr
                  key={t.id}
                  className="border-b border-background-200/60 hover:bg-background-100/40 transition-colors"
                >
                  <td className="px-4 py-3">
                    <span className="text-foreground-600 text-[10px] font-mono font-medium">{t.ticketNumber}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-foreground-800 text-xs font-semibold">{t.radioName}</p>
                      <p className="text-foreground-400 text-[10px]">{t.frequency}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 max-w-[220px]">
                    <p className="text-foreground-800 text-xs font-medium truncate">{t.subject}</p>
                    <p className="text-foreground-400 text-[10px] truncate">{t.description.slice(0, 60)}...</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex px-2 py-0.5 rounded-md bg-background-100 text-foreground-500 text-[10px] font-medium">
                      {categoryAbbr[t.category]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${priorityColorMap[t.priority]}`}>
                      {priorityLabels[t.priority]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusColorMap[t.status]}`}>
                      <i className={`${statusIcons[t.status]} text-[9px]`}></i>
                      {statusLabels[t.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {t.assignedTo ? (
                      <span className="text-foreground-600 text-[10px]">{t.assignedTo}</span>
                    ) : (
                      <span className="text-foreground-300 text-[10px]">Sin asignar</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-foreground-500 text-[10px]" title={formatDate(t.createdAt)}>
                      {timeAgo(t.createdAt)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => onView(t)}
                      className="w-7 h-7 rounded-lg bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors"
                    >
                      <i className="ri-eye-line text-foreground-500 text-xs"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      {/* Mobile cards */}
      <div className="lg:hidden space-y-2">
        {tickets.map((t) => (
          <div key={t.id} className="bg-background-50 border border-background-200 rounded-2xl p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-foreground-500 text-[10px] font-mono font-medium">{t.ticketNumber}</span>
                  <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[9px] font-semibold ${priorityColorMap[t.priority]}`}>
                    {priorityLabels[t.priority]}
                  </span>
                </div>
                <p className="text-foreground-800 text-xs font-semibold">{t.subject}</p>
                <p className="text-foreground-400 text-[10px]">{t.radioName} · {t.frequency}</p>
              </div>
              <button
                onClick={() => onView(t)}
                className="shrink-0 w-8 h-8 rounded-xl bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors"
              >
                <i className="ri-eye-line text-foreground-500 text-sm"></i>
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${statusColorMap[t.status]}`}>
                <i className={`${statusIcons[t.status]} text-[8px]`}></i>
                {statusLabels[t.status]}
              </span>
              <span className="inline-flex px-2 py-0.5 rounded-md bg-background-100 text-foreground-500 text-[9px] font-medium">
                {categoryAbbr[t.category]}
              </span>
              {t.assignedTo ? (
                <span className="text-foreground-500 text-[9px]">{t.assignedTo}</span>
              ) : (
                <span className="text-foreground-300 text-[9px]">Sin asignar</span>
              )}
              <span className="text-foreground-400 text-[9px] ml-auto">{timeAgo(t.createdAt)}</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}